import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, BACKEND_URL as BACKEND } from "@/lib/api";
import { CheckCircle2, FileText, Loader2, AlertTriangle } from "lucide-react";

const fmtDay = (d) => d.split("-").reverse().join("/");

function FieldInput({ f, value, onChange }) {
  const req = { required: !!f.required };
  const cls = "w-full border border-black/15 bg-white px-3 py-3 text-base outline-none focus:border-[#002FA7]";
  const tid = `signon-field-${f.id}`;
  if (f.type === "textarea") return <textarea {...req} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={tid} />;
  if (f.type === "select") return (
    <select {...req} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={tid}>
      <option value="">Select…</option>
      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (f.type === "checkbox") return (
    <label className="flex items-center gap-2 text-sm"><input {...req} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={tid} className="h-5 w-5" /> Yes</label>
  );
  return <input {...req} type={f.type === "tel" ? "tel" : f.type} inputMode={f.type === "tel" ? "tel" : undefined} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={tid} />;
}

export default function SignOn() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [values, setValues] = useState({});
  const [ack, setAck] = useState(false);
  const [day, setDay] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/events/${slug}/signon`).then(({ data }) => { setForm(data); setDay(data.days[0] || ""); })
      .catch((e) => setError(e.response?.data?.detail || "Sign-on is not available"));
  }, [slug]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    const { first_name, last_name, mobile, ...answers } = values;
    try {
      const { data } = await api.post(`/public/events/${slug}/signon`, { first_name, last_name, mobile, answers, disclaimer_ack: ack, day });
      setDone(data);
    } catch (err) { setError(err.response?.data?.detail || "Sign-on failed"); }
    finally { setBusy(false); }
  }

  if (!form) return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F4F5] p-6 text-center" data-testid="signon-loading">
      {error ? <div><AlertTriangle className="mx-auto h-8 w-8 text-[#FF3B30]" /><p className="mt-3 text-sm" data-testid="signon-unavailable">{error}</p></div> : <Loader2 className="h-6 w-6 animate-spin" />}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-lg px-5 py-4">
          <Link to="/" className="font-display text-xl font-black tracking-tighter">VOLMEE<span className="text-[#FF3B30]">.</span></Link>
        </div>
      </header>
      <main className="mx-auto max-w-lg px-5 py-8">
        <div className="text-xs uppercase tracking-widest text-[#002FA7]">{form.event.name}</div>
        <h1 className="mt-1 font-display text-4xl font-black tracking-tighter" data-testid="signon-title">{form.title}</h1>

        {done ? (
          <div className="mt-6 border-2 border-emerald-500 bg-white p-6" data-testid="signon-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <h2 className="mt-3 font-display text-2xl font-black tracking-tighter">You're signed on.</h2>
            <p className="mt-1 text-sm text-zinc-700">Signed on at <b data-testid="signon-success-time">{done.signed_at_local}</b>.</p>
            {done.matched ? (
              <ul className="mt-4 space-y-1 text-sm" data-testid="signon-success-elements">
                {done.elements.map((el) => <li key={el.session + el.role} className="border border-black/10 bg-zinc-50 px-3 py-2"><b>{el.session}</b> — {el.role}</li>)}
              </ul>
            ) : (
              <p className="mt-4 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="signon-not-on-roster">We couldn't find you on today's roster. Your sign-on has been recorded and the organiser will check it.</p>
            )}
          </div>
        ) : form.days.length === 0 ? (
          <p className="mt-6 border border-black/10 bg-white p-6 text-sm" data-testid="signon-closed">Sign-on opens on the day of the event.</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-5 border border-black/10 bg-white p-6 shadow-[6px_6px_0_0_rgba(0,0,0,0.05)]" data-testid="signon-form">
            {form.days.length > 1 ? (
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-600">Day</span>
                <select value={day} onChange={(e) => setDay(e.target.value)} className="w-full border border-black/15 px-3 py-3" data-testid="signon-day-select">
                  {form.days.map((d) => <option key={d} value={d}>{fmtDay(d)}</option>)}
                </select>
              </label>
            ) : null}
            {form.fields.map((f) => (
              <label key={f.id} className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-600">{f.label}{f.required ? <span className="text-[#FF3B30]"> *</span> : null}</span>
                <FieldInput f={f} value={values[f.id]} onChange={(v) => setValues({ ...values, [f.id]: v })} />
              </label>
            ))}
            {form.message ? (
              <div className="whitespace-pre-line border-l-4 border-[#FF3B30] bg-[#FF3B30]/[0.05] px-4 py-3 text-sm font-semibold text-zinc-900" data-testid="signon-message">{form.message}</div>
            ) : null}
            {form.has_disclaimer ? (
              <a href={`${BACKEND}/api/public/events/${slug}/signon/disclaimer`} target="_blank" rel="noreferrer" data-testid="signon-disclaimer-link" className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] underline underline-offset-2">
                <FileText className="h-4 w-4" /> Read the full disclaimer (optional)
              </a>
            ) : null}
            <label className="flex cursor-pointer items-start gap-3 border border-black/15 bg-zinc-50 p-3 text-sm" data-testid="signon-ack-label">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 h-5 w-5" data-testid="signon-ack-checkbox" />
              <span>I have read and acknowledge the statement above{form.has_disclaimer ? " and the disclaimer" : ""}.</span>
            </label>
            {error ? <div className="border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="signon-error">{error}</div> : null}
            <button type="submit" disabled={busy || !ack} data-testid="signon-submit-btn" className="brand-btn inline-flex w-full items-center justify-center gap-2 px-6 py-4 text-base font-semibold disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign on
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
